import { TenantMemberDisplayData, TenantMemberOrTeamDisplayData } from '../identity/types'
import { PaginatedSearch, Percentage, SerializedSort, WithPermissions } from '../shared/types'
import { ReactionValue, WallPost } from '../shared/wallPost/types'
import { Quality } from '../value'

export type CreateRecognitionParams = {
  message: string
  amount: number
  attachments: string[]
  qualityIds: string[]
  sendTo: SendTo[]
  gifs: string[]
  scheduledAt: string | null
  status: RecognitionStatus
  ignoreExceptions: boolean
  tenantWide: boolean
  refinementId?: string
}

export type RecognitionRecipients = SendTo & {
  displayData?: TenantMemberOrTeamDisplayData | null
}

export type Recognition = WithPermissions<
  WallPost & {
    status: RecognitionStatus
    qualityIds: string[]
    attachments: string[]
    tenantWide: boolean
    scheduledAt: string | null
    publishStartedAt: string | null
    errorState: ErrorState | null
    delegateId: string | null
    // Embeds
    qualities: Quality[]
    recipientCount: number
    ownRecipient: Recipient | null
    __entity_type__: 'recognition'
  }
>

export type RecognitionFlag = {
  id: string
  recognitionId: string
  flaggedById: string
  flaggedBy: TenantMemberDisplayData | null
  dismissedById: string | null
  dismissedAt: string | null
  createdAt: string
  __entity_type__: 'recognitionFlag'
}

export enum PointsExceptionType {
  RESTRICTION = 'restriction',
  INSUFFICIENT_POINTS = 'insufficientPoints',
  NOT_ELIGIBLE = 'notEligible',
}

export type PointsException = {
  type: PointsExceptionType
  affected: number
  level: 'warning' | 'error'
}

export type PointsExceptionError = {
  type: 'PointsExceptionError'
  msg: string
  details: {
    amount: number
    balance: number
    exceptions: PointsException[]
  }
}

export type RecognitionWithFlags = Recognition & {
  flags: RecognitionFlag[]
}

export type Recipient = {
  recognitionId: string
  memberId: string
  transactionId: string | null
  amount: number | null
  __entity_type__: 'recipient'
}

export interface ErrorState {
  error: string
  msg: string
}

export type SendTo = {
  id: string
  type: SendTargetType
  name: string
}

export enum SendTargetType {
  TENANT_MEMBER = 'tenantMember',
  TEAM = 'team',
}

export enum RecognitionStatus {
  DRAFT = 'draft',
  PUBLISHED = 'published',
}

export type RecognitionEmbeds =
  | 'attachmentUrls'
  | 'qualities'
  | 'recipientCount'
  | 'reactionCounts'
  | 'sendToAndSender'
  | 'comments'
  | 'endorsementStats'
  | 'ownRecipient'

export type RecognitionReaction = {
  recognitionId: string
  memberId: string
  reaction: ReactionValue
  memberDisplayData: TenantMemberDisplayData | null
  createdAt: string
  updatedAt: string
  __entity_type__: 'reaction'
}

export type CreateQualityParams = {
  name: string
  valueId: string
}

export type ListRecognitionRecipientsSummaryParams = PaginatedSearch<
  {
    days?: number
    ownReports?: boolean
  },
  'members'
>

export type RecognitionSummaryRecipient = {
  memberId: string
  receivedCount: number
  memberDisplayData: TenantMemberDisplayData | null
  lastRecognizedAt: string | null
}

export type PreviewRecipientsResults = {
  recipientCount: number
}

export type RecognitionParams = {
  idIn?: string[]
  statusIn?: RecognitionStatus[]
  senderIdIn?: string[]
  recipientIdIn?: string[]
  sentByOrToIdIn?: string[]
  publishedAfter?: Date
}

export type ListRecognitionsParams = PaginatedSearch<RecognitionParams, RecognitionEmbeds>

/**
 * Embeds supported by the draft-only endpoint `GET /recognition/recognitions/drafts`.
 * Deliberately narrower than {@link RecognitionEmbeds}: interaction-related embeds
 * (reactions, comments, endorsements) are omitted because they cannot exist on a draft.
 */
export type DraftRecognitionEmbeds =
  | 'attachmentUrls'
  | 'qualities'
  | 'recipientCount'
  | 'sendToAndSender'

/**
 * Params for the draft-only list endpoint. The backend scopes results to the
 * caller's own drafts, so no filter params are accepted. Sorting is limited to
 * `createdAt`/`id` since `publishedAt` is always null for drafts.
 */
export type ListDraftRecognitionsParams = PaginatedSearch<
  Record<never, never>,
  DraftRecognitionEmbeds,
  never,
  SerializedSort<'createdAt' | 'id'>
>

export type FlagCommentParams = {
  id: string
  commentId: string
}

export type RecognitionEndorsement = {
  recognitionId: string
  memberId: string
  amount: number
  memberDisplayData: TenantMemberDisplayData | null
  createdAt: string
  __entity_type__: 'endorsement'
}

export type RecognitionGoal = {
  count: number
  goal: number
}

export type ManagerRankingData = {
  rank: number
  value: number
  total: number
  percent: Percentage
  memberDisplayData: TenantMemberDisplayData
  diff: {
    percent: Percentage
  }
}

export type TopSendRate = {
  type: 'topSendRate'
  data: ManagerRankingData[]
  updatedAt: string
}

export type BottomSendRate = {
  type: 'bottomSendRate'
  data: ManagerRankingData[]
  updatedAt: string
}

export type AverageSendRate = {
  type: 'averageSendRate'
  updatedAt: string
  data: { percent: Percentage; diff: { percent: Percentage | null } }
}

export type MySendRate = {
  type: 'mySendRate'
  data: {
    ranks: number | null
    total: number
    diff: {
      percent: Percentage
    }
  }
  updatedAt: string
}

export type ManagerRanking = [TopSendRate, BottomSendRate, MySendRate, AverageSendRate]

export type ManagerDirectReportRanking = {
  id: string
  count: number
  memberDisplayData: TenantMemberDisplayData | null
  diff: {
    percent: Percentage | null
  }
}

export type PeerRecognition = {
  memberId: string
  memberDisplayData: TenantMemberDisplayData
  lastRecognizedAt: null | string
}

export enum PointPresetTypeEnum {
  ENDORSEMENT = 'endorsement',
  RECOGNITION = 'recognition',
}

export type PointPresetParam = {
  type: PointPresetTypeEnum
  allowCustomPoints: boolean
  presetPoints: number[]
}

export type ListPointPresetsParams = {
  typeIn?: PointPresetTypeEnum[]
}

export type PointPreset = {
  tenantId: string
  allowCustomPoints: boolean
  type: PointPresetTypeEnum
  presetPoints: number[]
  createdAt: string
  updatedAt: string
  __entity_type__: 'pointPreset'
}
